import { useEffect } from "react";
import Header from "../../layouts/Header";
import AutoStackPerformanceChart from "../../components/AutoStackPerformanceChart";
import MyStacksCard from "../../components/MyStacksCard";
import { Typography } from "../../components/ui/typography";
import {
  useAutoStackStats,
  useUserAutoStacks,
} from "../../hooks/query/queries";
import { useAssetsStore } from "../../store/use-assets-store";
import { UserAutoStackResponse } from "../../types/generated/api/types.gen";
import { Asset } from "../../types/asset";
import AutostackOverallSkeleton from "../../components/skeletons/AutostackOverallSkeleton";
import MyStacksSectionSkeleton from "../../components/skeletons/MyStacksSectionSkeleton";

export default function AutostackOverall() {
  const {
    data: autoStackStats,
    isLoading: isLoadingAutoStackStats,
    isFetching: isFetchingAutoStackStats,
  } = useAutoStackStats();
  const {
    data: userAutoStacks,
    isLoading: isLoadingUserStacks,
    isFetching: isFetchingUserStacks,
    error: errorUserStacks,
  } = useUserAutoStacks();

  const { fetchAssets, assets } = useAssetsStore();

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  const findAssetBySymbol = (assetSymbol: Asset) => {
    return assets?.find(
      (asset) => asset.asset.toLowerCase() === assetSymbol.asset.toLowerCase()
    );
  };

  if (
    isLoadingAutoStackStats ||
    isFetchingAutoStackStats ||
    isLoadingUserStacks ||
    isFetchingUserStacks
  ) {
    return (
      <>
        <Header />
        <AutostackOverallSkeleton />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="px-6 md:px-14 py-6 bg-bg-light-gray rounded-t-2xl space-y-8 h-[calc(100vh-100px)]">
        <div className="flex items-start flex-wrap md:flex-nowrap gap-6 w-full">
          <div className="w-full md:w-1/2">
            <AutoStackPerformanceChart
              autoStackStats={autoStackStats}
              isLoading={isLoadingAutoStackStats || isFetchingAutoStackStats}
              chartHeight="h-80"
              overallAutoStack
            />
          </div>
          <div className="w-full md:w-1/2">
            <div className="bg-white rounded-2xl p-6 border border-bg-section max-h-[calc(100vh-140px)] overflow-y-auto">
              <div className="flex flex-col">
                <Typography
                  variant="h2"
                  className="text-lg md:text-xl font-semibold text-text-secondary mb-4"
                >
                  My Stacks
                </Typography>

                {isLoadingUserStacks || isFetchingUserStacks ? (
                  <MyStacksSectionSkeleton />
                ) : errorUserStacks ? (
                  <div className="text-center p-8 text-text-secondary">
                    <Typography variant="p" className="text-sm">
                      Error loading stacks
                    </Typography>
                  </div>
                ) : userAutoStacks?.results &&
                  userAutoStacks.results.length > 0 ? (
                  <div className="space-y-4">
                    {userAutoStacks.results.map(
                      (stack: UserAutoStackResponse) => (
                        <MyStacksCard
                          key={stack._id}
                          stack={stack}
                          asset={findAssetBySymbol(stack.asset as Asset)}
                        />
                      )
                    )}
                  </div>
                ) : (
                  <div className="text-center p-8 text-text-secondary">
                    <Typography variant="p" className="text-sm">
                      No stacks found
                    </Typography>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
